import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../api/users';
import LeagueStatusBadge from './LeagueStatusBadge';

interface Props {
  userId: string;
}

export default function PlayedEvents({ userId }: Props) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['played-events', userId],
    queryFn: () => usersApi.getPlayedEvents(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const seasons = data?.seasons ?? [];
  const leagues = data?.leagues ?? [];

  if (isLoading) {
    return (
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.playedEvents')}</h2>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse h-12 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (seasons.length === 0 && leagues.length === 0) return null;

  return (
    <div className="card mt-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.playedEvents')}</h2>
      <div className="space-y-2">
        {seasons.map((s) => (
          <Link
            key={`season-${s.id}`}
            to={`/seasons/${s.id}`}
            className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <div>
              <p className="font-medium text-gray-900 text-sm">{s.name}</p>
              <p className="text-xs text-gray-500">
                {t('profile.historySeason')} · {t('season.class')} {s.season_class}
              </p>
            </div>
            <span className="badge border text-xs bg-gray-100 text-gray-500 border-gray-300">
              {t('season.status_finished')}
            </span>
          </Link>
        ))}
        {leagues.map((l) => (
          <Link
            key={`league-${l.id}`}
            to={`/leagues/${l.id}`}
            className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <div>
              <p className="font-medium text-gray-900 text-sm">{l.name}</p>
              <p className="text-xs text-gray-500">
                {t('profile.historyLeague')}{l.level ? ` · ${l.level}` : ''}
              </p>
            </div>
            <LeagueStatusBadge status="finished" />
          </Link>
        ))}
      </div>
    </div>
  );
}
